/**
 * Synthia 插件注册器实现
 * 负责插件的注册、管理和命令注册
 */

import {
  SynthiaPlugin,
  PluginCommand,
  PluginRegistry,
  PluginError,
  PluginValidationResult,
  PluginValidator,
} from './plugin.js';

/**
 * 插件注册器实现
 */
export class DefaultPluginRegistry implements PluginRegistry {
  private plugins = new Map<string, SynthiaPlugin>();
  private commands = new Map<string, PluginCommand>();
  private commandToPlugin = new Map<string, string>();

  constructor(private validator?: PluginValidator) {}

  /**
   * 注册插件
   */
  async register(plugin: SynthiaPlugin): Promise<void> {
    try {
      // 验证插件
      if (this.validator) {
        const validation = this.validator.validate(plugin);
        if (!validation.valid) {
          throw new PluginError(
            `插件验证失败: ${validation.errors.join(', ')}`,
            plugin.metadata.name,
            'VALIDATION_FAILED'
          );
        }
      }

      // 检查插件是否已存在
      if (this.plugins.has(plugin.metadata.name)) {
        throw new PluginError(
          `插件 ${plugin.metadata.name} 已存在`,
          plugin.metadata.name,
          'PLUGIN_EXISTS'
        );
      }

      // 注册插件
      this.plugins.set(plugin.metadata.name, plugin);

      // 注册插件命令
      const commandNames = [];
      for (const command of plugin.commands) {
        await this.registerCommand(plugin.metadata.name, command);
        commandNames.push(command.name);
      }

      // 静默注册插件，不显示成功信息
    } catch (error) {
      console.error(`❌ 插件 ${plugin.metadata.name} 注册失败:`, error);
      throw error;
    }
  }

  /**
   * 注销插件
   */
  async unregister(pluginName: string): Promise<void> {
    const plugin = this.plugins.get(pluginName);
    if (!plugin) {
      throw new PluginError(
        `插件 ${pluginName} 不存在`,
        pluginName,
        'PLUGIN_NOT_FOUND'
      );
    }

    // 注销插件命令
    for (const command of plugin.commands) {
      this.unregisterCommand(command.name);
    }

    // 移除插件
    this.plugins.delete(pluginName);

    console.log(`✅ 插件 ${pluginName} 注销成功`);
  }

  /**
   * 获取插件
   */
  get(pluginName: string): SynthiaPlugin | undefined {
    return this.plugins.get(pluginName);
  }

  /**
   * 获取所有插件
   */
  getAll(): SynthiaPlugin[] {
    return Array.from(this.plugins.values());
  }

  /**
   * 检查插件是否存在
   */
  has(pluginName: string): boolean {
    return this.plugins.has(pluginName);
  }

  /**
   * 获取插件命令
   */
  getCommands(): PluginCommand[] {
    return Array.from(this.commands.values());
  }

  /**
   * 注册插件命令
   */
  private async registerCommand(
    pluginName: string,
    command: PluginCommand
  ): Promise<void> {
    // 检查命令是否已存在
    if (this.commands.has(command.name)) {
      const existingPlugin = this.commandToPlugin.get(command.name);
      throw new PluginError(
        `命令 ${command.name} 已被插件 ${existingPlugin} 注册`,
        pluginName,
        'COMMAND_EXISTS'
      );
    }

    // 注册命令
    this.commands.set(command.name, command);
    this.commandToPlugin.set(command.name, pluginName);

    // 注册命令别名
    if (command.aliases) {
      for (const alias of command.aliases) {
        if (this.commands.has(alias)) {
          const existingPlugin = this.commandToPlugin.get(alias);
          throw new PluginError(
            `命令别名 ${alias} 已被插件 ${existingPlugin} 注册`,
            pluginName,
            'ALIAS_EXISTS'
          );
        }
        this.commands.set(alias, command);
        this.commandToPlugin.set(alias, pluginName);
      }
    }

    // 命令注册成功，不输出日志避免重复
  }

  /**
   * 注销插件命令
   */
  private unregisterCommand(commandName: string): void {
    const command = this.commands.get(commandName);
    if (!command) return;

    // 移除命令
    this.commands.delete(commandName);
    this.commandToPlugin.delete(commandName);

    // 移除命令别名
    if (command.aliases) {
      for (const alias of command.aliases) {
        this.commands.delete(alias);
        this.commandToPlugin.delete(alias);
      }
    }

    console.log(`✅ 命令 ${commandName} 注销成功`);
  }

  /**
   * 获取命令所属的插件
   */
  getCommandPlugin(commandName: string): string | undefined {
    return this.commandToPlugin.get(commandName);
  }

  /**
   * 获取插件的命令列表
   */
  getPluginCommands(pluginName: string): PluginCommand[] {
    const plugin = this.plugins.get(pluginName);
    return plugin ? plugin.commands : [];
  }
}

/**
 * 插件验证器实现
 */
export class DefaultPluginValidator implements PluginValidator {
  /**
   * 验证插件
   */
  validate(plugin: SynthiaPlugin): PluginValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // 验证元数据
    const metadataValidation = this.validateMetadata(plugin.metadata);
    errors.push(...metadataValidation.errors);
    warnings.push(...metadataValidation.warnings);

    // 验证命令
    const commandsValidation = this.validateCommands(plugin.commands);
    errors.push(...commandsValidation.errors);
    warnings.push(...commandsValidation.warnings);

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * 验证插件元数据
   */
  validateMetadata(metadata: any): PluginValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // 必需字段检查
    if (!metadata.name) {
      errors.push('插件名称不能为空');
    } else if (!/^[a-z0-9-]+$/.test(metadata.name)) {
      errors.push('插件名称只能包含小写字母、数字和连字符');
    }

    if (!metadata.version) {
      errors.push('插件版本不能为空');
    } else if (!/^\d+\.\d+\.\d+/.test(metadata.version)) {
      errors.push('插件版本格式不正确，应为 x.y.z 格式');
    }

    if (!metadata.description) {
      errors.push('插件描述不能为空');
    }

    // 可选字段检查
    if (metadata.author && typeof metadata.author !== 'string') {
      errors.push('插件作者必须是字符串');
    }

    if (metadata.license && typeof metadata.license !== 'string') {
      errors.push('插件许可证必须是字符串');
    }

    if (metadata.keywords && !Array.isArray(metadata.keywords)) {
      errors.push('插件关键词必须是数组');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * 验证插件命令
   */
  validateCommands(commands: any[]): PluginValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!Array.isArray(commands)) {
      errors.push('插件命令必须是数组');
      return { valid: false, errors, warnings };
    }

    if (commands.length === 0) {
      warnings.push('插件没有定义任何命令');
    }

    const commandNames = new Set<string>();
    const commandAliases = new Set<string>();

    for (const command of commands) {
      // 验证命令结构
      if (!command.name) {
        errors.push('命令名称不能为空');
        continue;
      }

      if (!/^[a-z0-9-]+$/.test(command.name)) {
        errors.push(`命令名称 ${command.name} 只能包含小写字母、数字和连字符`);
      }

      if (commandNames.has(command.name)) {
        errors.push(`命令名称 ${command.name} 重复`);
      } else {
        commandNames.add(command.name);
      }

      if (!command.description) {
        errors.push(`命令 ${command.name} 的描述不能为空`);
      }

      if (!command.action || typeof command.action !== 'function') {
        errors.push(`命令 ${command.name} 的 action 必须是函数`);
      }

      // 验证命令别名
      if (command.aliases) {
        if (!Array.isArray(command.aliases)) {
          errors.push(`命令 ${command.name} 的别名必须是数组`);
          continue;
        }

        for (const alias of command.aliases) {
          if (!/^[a-z0-9-]+$/.test(alias)) {
            errors.push(
              `命令 ${command.name} 的别名 ${alias} 只能包含小写字母、数字和连字符`
            );
          }

          if (commandAliases.has(alias)) {
            errors.push(`命令别名 ${alias} 重复`);
          } else {
            commandAliases.add(alias);
          }
        }
      }

      // 验证命令选项
      if (command.options) {
        if (!Array.isArray(command.options)) {
          errors.push(`命令 ${command.name} 的选项必须是数组`);
        } else {
          for (const option of command.options) {
            if (!option.flags) {
              errors.push(`命令 ${command.name} 的选项缺少 flags`);
            }
            if (!option.description) {
              errors.push(`命令 ${command.name} 的选项缺少 description`);
            }
          }
        }
      }

      // 验证命令参数
      if (command.arguments) {
        if (!Array.isArray(command.arguments)) {
          errors.push(`命令 ${command.name} 的参数必须是数组`);
        } else {
          for (const arg of command.arguments) {
            if (!arg.name) {
              errors.push(`命令 ${command.name} 的参数缺少 name`);
            }
            if (!arg.description) {
              errors.push(`命令 ${command.name} 的参数缺少 description`);
            }
          }
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }
}
